import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { GL_MAX_COL_AMOUNT, GL_MAX_FILE_SIZE_MB, GL_MAX_ROW_AMOUNT }
  from '../../domain/financial-statement/services/gl-upload/gl-upload.service';
import {
  INVESTORS_MAX_ROW_AMOUNT,
} from '../../domain/financial-statement/modals/capital-account/manage-investors-modal/manage-investors-modal.component';

export interface NotificationContent {
  title: string;
  message: string;
  timeout?: number;
}

/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-len */
/* eslint-disable no-trailing-spaces */
/**
 * Predifined notifications content.
 * Add notifications content here to use it in the notifications service.
 *
 * New fields should be named by this pattern - `{CONTEXT}_{SUCCESS|ERROR|WARNING|INFO}`.
 *
 * **SUCCESS** - something is done and we need to tell user about it.
 * **ERROR** - something went wrong and we need to tell user about it.
 * **WARNING** - something will be done but user need to understand some consequences of this action.
 * **INFO** - some information about the action, not so important, but we want to show it to user anyway.
 *
 * @example
 * this.notifications.success(NOTIFICATIONS.PASSWORD_RESET_SUCCESS);
 */
/* eslint-enable no-trailing-spaces */
export const NOTIFICATIONS = {
  // COMMON
  FORBIDDEN_ERROR: { title: 'Forbidden', message: 'You are not allowed to perform this action.' },
  NETWORK_ERROR: { title: 'Network error', message: 'Something went wrong, please try again.' },
  LOGOUT_ERROR: { title: 'Unable to perform the logout.', message: 'Please close the tab/browser or contact SerVC support.' },
  SAVE_SUCCESS: { title: 'Save successful', message: 'Data saved successfully' },
  SAVE_UNSUCCESSFUL: { title: 'Save unsuccessful', message: 'Data not saved successfully' },
  COPIED: { title: 'Save successful', message: 'Copied to clipboard' },
  COPIED_NO_TITLE: { title: 'Copied to clipboard', message: '' },
  DELETE_SUCCESS: { title: 'Delete successful', message: 'Data deleted successfully' },
  METHOD_NOT_IMPLEMENTED: { title: 'The method is not implemented', message: 'Please implement it' },
  INSUFFICIENT_DATA: { title: 'Insufficient data', message: 'Please fill in the data before sending a request' },

  // VALIDATION
  WRONG_EMAIL_ERROR: { title: 'Wrong email', message: 'Please enter a valid email address.' },
  PASSWORDS_NOT_EQUAL_ERROR: { title: 'Passwords do not match', message: 'Please enter again.' },
  PASSWORD_TOO_SIMPLE_ERROR: { title: 'Password too simple', message: 'Password must contain at least 8 characters, at least one digit, at least one lowercase and at least one uppercase letter.' },
  PASSWORD_NOT_IN_LATIN_ERROR: { title: 'Password contains not latin characters', message: 'Password must contain only Latin characters and symbols.' },
  VERIFICATION_CODE_IS_TOO_SHORT_ERROR: { title: 'Verification code is too short', message: 'Please enter verification code.' },
  PATTERN_BULK_PP_INPUT_NOT_CORRECT: { title: 'Not correct input pattern', message: 'There should be a number before XX. XX - will be replaced by Company ID' },
  SET_NEW_PHONE_NUMBER_SUCCESS: { title: 'Phone changed', message: 'Your phone number has been changed successfully.' },
  PHONE_CONTAINS_NOT_ALLOWED_SYMBOLS_ERROR: { title: 'Not allowed phone format', message: 'Phone contains not allowed symbols. Please enter in format +431234567890' },
  SET_NEW_WHATSAPP_VERIFICATION_METHOD_SUCCESS: { title: 'Verification method updated', message: 'Please use WhatsApp for next verification' },
  SET_NEW_SMS_VERIFICATION_METHOD_SUCCESS: { title: 'Verification method updated', message: 'Please use SMS for next verification' },

  // LOGIN PAGE
  FORGOT_PASSWORD_SUCCESS: { title: 'Password reset', message: 'Your password has been reset. Please check your email.' },
  FORGOT_PASSWORD_ERROR: { title: 'Password reset', message: 'Your password has not been reset. Something went wrong, please try again.' },
  SET_NEW_PASSWORD_SUCCESS: { title: 'Password saved', message: 'Password has been saved successfully. Redirecting to login page.' },
  SET_NEW_PASSWORD_AGR_ERROR: { title: 'Password was not saved', message: 'Password cannot be saved without agreement of terms of use.' },
  WRONG_EMAIL_OR_PASSWORD_ERROR: { title: 'Wrong email or password', message: 'Please check your email and password and try again.' },
  RESEND_SMS_CODE_SUCCESS: { title: 'Verification code sent', message: 'Verification code has been sent to your phone number.' },
  RESEND_WHATSAPP_CODE_SUCCESS: { title: 'Verification code sent', message: 'Verification code has been sent to your WhatsApp.' },
  VERIFY_SMS_CODE_ERROR: { title: 'Invalid code', message: 'Please check your code and try again.' },
  CAPTCHA_NOT_RESOLVED_ERROR: { title: 'Unresolved captcha', message: 'Please resolve captcha and try again.' },
  CAPTCHA_BAD_SCORE_ERROR: { title: 'Captcha error', message: 'Please resolve captcha and try again.' },
  EMPTY_EMAIL_ERROR: { title: 'Empty email', message: 'Please enter your email.' },
  EMPTY_PASSWORD_OR_EMAIL_ERROR: { title: 'Empty password or email', message: 'Please enter your password and email.' },
  DEMO_REQUEST_SUCCESS: { title: 'Demo request sent', message: 'Thanks for your interest, we\'ll contact you.' },
  UNAVAILABLE_FOR_LEGAL_REASONS: { title: 'Unavailable For Legal Reasons', message: 'There is an updated version of the Terms and Conditions' },

  // COMPANIES
  COMPANY_DUPLICATE_NAME_ERROR: { title: 'Duplicate company name', message: 'Company name already exists. Please choose another name.' },
  WRONG_PICTURE_EXTENSION_ERROR: { title: 'Wrong file extension', message: 'Please upload a picture with the following extensions: jpg, jpeg, png.' },
  PICTURE_SIZE_EXCEEDED_ERROR: { title: 'Picture size exceeded', message: 'Image should be less than 5MB.' },
  ROUNDS_NOT_EMPTY_ERROR: { title: 'Rounds not empty', message: 'Please delete all rounds before deleting the company.' },
  COMPANY_UPDATE_SUCCESS: { title: 'Update successful', message: 'Company was updated successfully.' },
  COMPANY_NOT_FOUND: { title: 'Search result is empty ', message: 'No company found.' },

  // FINANCIAL STATEMENT
  COLUMN_SIZE_EXCEEDED_ERROR: { title: 'Column size exceeded', message: 'Please upload a file with a size less than ' + GL_MAX_COL_AMOUNT + ' columns.' },
  ROW_SIZE_EXCEEDED_ERROR: { title: 'Row size exceeded', message: 'Please upload a file with a size less than ' + GL_MAX_ROW_AMOUNT + ' rows.' },
  WRONG_FILE_EXTENSION_ERROR: { title: 'Wrong file extension', message: 'Please upload a file with the following extension: xlsx.' },
  CANT_CONVERT_ENTRY_WITH_MANUAL_ACCOUNT: { title: 'Manual account in entry', message: 'Please convert associated manual account first.' },
  CANT_DELETE_ACCOUNT_WITH_ENTRIES: { title: 'Manual account has entries', message: 'Please remove associated entries before deletion.' },
  FILE_SIZE_TOO_LARGE: { title: 'File too large', message: 'Please check your file size, if it is over ' + GL_MAX_FILE_SIZE_MB + 'MB, please use a different file.' },
  FS_IS_LOCKED_ERROR: { title: 'Financial statement is locked', message: 'This financial statement is locked and cannot be altered. Please unlock and try again.' },
  GL_FILE_NOT_FOUND: { title: 'No GL file found', message: 'Please make sure you are uploading a gl.' },

  // PERIODS
  PERIOD_ALREADY_EXISTS_ERROR: { title: 'Period already exists', message: 'Period already exists. Please choose another period.' },
  PERIOD_IS_LOCKED_ERROR: { title: 'Period is locked', message: 'Unlock this period first' },
  ALL_PERIODS_ARE_UNLOCKED_SUCCESS: { title: '', message: 'All periods are unlocked successfully.' },
  ALL_PERIODS_ARE_BLOCKED_SUCCESS: { title: '', message: 'All periods are blocked successfully.' },
  PERIOD_IS_BLOCKED_SUCCESS: { title: '', message: 'Period is blocked successfully.' },
  PERIOD_IS_UNLOCKED_SUCCESS: { title: '', message: 'Period is unlocked successfully.' },
  PERIOD_IS_HIDDEN_SUCCESS: { title: '', message: 'Period is hidden successfully.' },
  PERIOD_IS_VISIBLE_SUCCESS: { title: '', message: 'Period set as visible successfully.' },
  PERIOD_IS_SET_PREDICTED_SUCCESS: { title: '', message: 'Period set as predicted successfully.' },
  PERIOD_IS_UNSET_PREDICTED_SUCCESS: { title: '', message: 'The period has been successfully unset as predicted.' },
  NO_VISIBLE_PERIODS: { title: '', message: 'The fund has no visible periods.' },
  PERIOD_WAS_CHANGED: { title: '', message: 'Period was changed.' },

  // BOX FILES
  FOLDER_EMPTY_ON_DOWNLOAD_ERROR: { title: 'Something went wrong', message: 'Please make sure that the folder you are downloading contains at least one file.' },
  FOLDER_CANT_BE_DELETED_ERROR: { title: 'Can\'t delete folder', message: 'You may not delete company folders. You may only delete their contents.' },
  FOLDER_PROTECTED_ERROR: { title: 'Protected folder', message: 'At least one of your items was not moved because it was a Company folder, which are protected.' },
  RENAME_NOT_ALLOWED_ERROR: { title: 'Rename not allowed', message: 'You are not allowed to rename that item.' },
  UPLOAD_ERROR: { title: 'Upload Error', message: 'Oops! Drag error, try upload manually.' },
  FOLDER_NAME_ALREADY_IS_USED: { title: 'Creating folder error', message: 'A folder with this name already exists' },
  RENAME_FOLDER_NAME_ALREADY_IS_USED: { title: 'Rename error', message: 'A folder with this name already exists' },
  RENAME_FILE_NAME_ALREADY_IS_USED: { title: 'Rename error', message: 'A file with this name already exists' },
  NO_FILE_SELECTED_FOR_DOCS_CHAT: { title: 'No file selected', message: 'Please select a file to open it in chat' },

  // DATA ROOM
  USER_IS_ALREADY_INVITED: { title: 'User is already invited to folder', message: 'Please chose another one' },
  NO_TEAM_MEMBERS: { title: 'Organization does not have team members with data room access', message: 'You may update it in organization settings' },

  // NOTIFICATION MODAL
  ADD_NEW_NOTIFICATION_SUCCESS: { title: 'Notification added', message: 'Successfully added a new notification.' },
  UPDATE_NOTIFICATION_SUCCESS: { title: 'Notification updated', message: 'Successfully updated the notification.' },
  DELETE_NOTIFICATION_SUCCESS: { title: 'Notification deleted', message: 'Successfully deleted the notification.' },
  FUND_CREATED_SUCCESS: { title: 'Fund created', message: 'Successfully created fund' },
  UPDATE_NOTIFICATION_ERROR: { title: 'Error', message: 'Could not update the notification' },
  CREATE_NOTIFICATION_ERROR: { title: 'Error', message: 'Could not create the notification' },
  DEADLINE_NOTIFICATION_ERROR: { title: 'Error', message: 'Deadline date should be after reminder date' },

  // BUDGET MODEL
  BUDGET_MODEL_DELETED_SUCCESSFULLY: { title: 'Model deleted', message: 'Fund budget model was deleted successfully' },
  BUDGET_MODEL_WAS_CREATED: { title: 'Model created', message: 'Fund budget model was created successfully' },
  BUDGET_MODEL_UPDATED_SUCCESSFULLY: { title: 'Model updated', message: 'Fund budget model was updated successfully' },
  BUDGET_MODEL_DUPLICATED_SUCCESSFULLY: { title: 'Model duplicated', message: 'Fund budget model was duplicated successfully' },

  // FUND STRATEGY MODEL
  EMPTY_GROUP_NAME_WARNING: { title: 'Group name is required', message: 'Please enter group name' },
  EMPTY_SUBGROUP_NAME_WARNING: { title: 'Subgroup name is required', message: 'Please enter subgroup name' },
  SAVE_MODEL_FIRST_WARNING: { title: 'Unsaved model', message: 'Save model and create the model name first.' },
  UNSAVED_CHANGES_WARNING: { title: 'Unsaved changes', message: 'You have unsaved changes, please save it first.' },
  WRITE_OFF_IS_TO_BIG_WARNING: { title: 'Wrong write-off value', message: 'Write-off is bigger than companies number.' },
  SAVE_ROUNDS_BEFORE_LEAVING_WARNING: { title: 'You are in edit mode', message: 'Save your changes before leaving.' },
  UNSAVED_YEARS_NUMBERS_WARNING: { title: 'Unsaved changes', message: 'Please save Fund term, years first.' },
  MAX_YEARS_NUMBERS_WARNING: { title: 'Not allowed value', message: 'Maximal number of years is 20.' },
  UNSAVED_COMMITMENT_WARNING: { title: 'Unsaved changes', message: 'Please save total commitment first.' },
  UNSAVED_GROUP_NAME_WARNING: { title: 'Unsaved changes', message: 'Please save group name.' },

  // ORGANIZATIONS
  ORGANIZATION_NAME_ALREADY_EXIST_ERROR: { title: 'Organization name already exists', message: 'This name of organization already exists, please choose another name.' },
  ORGANIZATION_ID_ALREADY_EXIST_ERROR: { title: 'Organization id already exists', message: 'This id of organization already exists, please choose another id.' },
  ORGANIZATION_ID_IS_ABSENT_ERROR: { title: 'Organization id is absent', message: 'This id of organization is absent please generate id.' },
  ORGANIZATION_NAME_IS_ABSENT_ERROR: { title: 'Organization name is absent', message: 'The name of organization is absent, please create name.' },
  DELETE_ORGANIZATION_SUCCESS: { title: 'Organization deleted', message: 'Organization deleted successfully .' },

  // BULK UPLOAD
  BULK_UPLOAD_NO_DATA_WARNING: { title: 'No company data found', message: 'Please check your data and try again' },
  BULK_UPLOAD_DOWNLOAD_STARTED_SUCCESS: { title: 'Download started', message: 'Check template and upload with your data' },

  // Fund Performance modal Update
  FUNDPERFORMANCE_DELETE_SUCCESS: { title: '', message: 'The period was deleted successfully.' },
  OTHER_EXPENCES_SHOULD_BE_NEGATIVE: { title: '', message: 'Aggregated other op. expenses should be negative.' },
  PARTNERS_CAPITAL_IS_NEGATIVE: { title: '', message: 'Partners capital (NAV) is negative.' },

  // TEAMS
  INVITE_SENT_SUCCESS: { title: 'Invite sent', message: 'Please ask user to check his email.' },
  DUPLICATE_EMAIL_ERROR: { title: 'Duplicate email', message: 'Team member with this email already exists.' },
  NAME_IS_SHORT_ERROR: { title: 'Name is to short', message: 'Enter a valid name.' },

  // Rounds
  ROUND_CREATED_SUCCESS: { title: 'Round created', message: 'Round was created successfully.' },
  ROUNDS_WILL_BE_DELETED_WARNING: { title: 'Please note', message: 'Existing rounds will be deleted.' },
  MAX_ATTEMPTS_REACHED: { title: 'You have reached limit of attempts', message: 'Please try again later.' },
  NO_ROUND_NAME_WARNING: { title: 'Please note', message: 'Add round name' },
  COMPANY_ACCOUNTING_ID_ALREADY_EXIST: { title: 'Company ID already exist in this fund', message: 'Please enter another ID for the company' },
  COMPANY_ACCOUNTING_CANT_BE_EMPTY: { title: 'The company ID cannot be empty.', message: 'Please check that the company ID is filled in.' },
  TOTAL_LOAN_SIZE_SHOULD_BE_BIGGER_THAN_PURCHASE_AMOUNT: { title: 'Total loan is wrong.', message: 'Total loan cannot be less than the purchase amount' },
  ROUND_UPDATED_SUCCESS: { title: 'Round updated', message: 'Round was updated successfully.' },
  ROUND_NOT_FOUND_ERROR: { title: 'Round not found', message: 'Round was not found.' },
  NO_VALID_ROUND_PROVIDED: { title: 'Unable to save rounds', message: 'Please make sure at least one valid round is provided.' },

  //  SEARCH
  EMPTY_SEARCH_RESULT: { title: 'No result', message: 'No data found.' },

  // Investor Report
  REPORT_CREATED: { title: 'Report created', message: 'Report was created successfully.' },
  REPORT_UPDATED: { title: 'Report updated', message: 'Report was updated successfully.' },
  REPORT_NOT_FOUND: { title: 'No report yet', message: 'You can create a new report on this period.' },
  NO_REPORTED_PERIODS: { title: 'No reports', message: 'No reported periods were found.' },
  NOTE_IS_TO_LONG: { title: 'The note is too long', message: 'Please make it shorter to save changes' },
  SAVING_EMPTY_PAGE_IS_NOT_ALLOWED: { title: 'Saving is not allowed', message: 'Please add companies or unselect the company tab.' },

  // Client
  CLIENT_HAS_ORGANIZATION: { title: 'Delete error', message: 'Client has organization' },

  // Metrics
  METRICS_UPDATED_SUCCESS: { title: 'Success', message: 'Company metrics were updated successfully.' },
  METRICS_UPDATED_ERROR: { title: 'Error', message: 'Could not update company metrics.' },
  METRICS_DELETED_SUCCESS: { title: 'Success', message: 'Company metrics were deleted successfully.' },
  METRICS_DELETED_ERROR: { title: 'Error', message: 'Could not delete company metrics.' },
  METRICS_UPLOADED_SUCCESS: { title: 'Success', message: 'Metrics were uploaded successfully.' },
  METRICS_UPLOADED_ERROR: { title: 'Error', message: 'Could not upload company metrics.' },
  METRICS_PERIODS_CREATED_SUCCESS: { title: 'Success', message: 'Periods were created successfully.' },
  METRICS_PERIODS_CREATE_WARNING: { title: 'Warning', message: 'Select at least one period and at least one company.' },
  METRICS_PERIODS_DELETED_SUCCESS: { title: 'Success', message: 'Periods were deleted successfully.' },
  DEFAULT_METRIC_GROUP_CREATED_SUCCESS: { title: 'Success', message: 'Default metric group is at the bottom of the list' },
  DEFAULT_METRIC_GROUP_DELETED_SUCCESS: { title: 'Success', message: 'Default metric group was deleted successfully.' },
  METRICS_RESET_WARNING: { title: 'Metrics are reset', message: 'All previous metrics data will be deleted', timeout: 7000 },

  // Cash flows
  CASH_FLOW_UPDATE_COMMENT_MAX_SIZE_ERROR: { title: 'Error', message: 'Comment is too long. Please make it shorter.' },
  CASH_FLOW_UPDATE_COMMENT_SUCCESS: { title: 'Success', message: 'Comment was updated successfully.' },
  NO_CASH_FLOW_WARNING: { title: 'No cash flows found', message: 'Please add cash flows' },

  // Portfolio performance
  NO_PORTFOLIO_WARNING: { title: 'No portfolios found', message: 'Please add rounds' },

  // Pinned documents
  DOWNLOAD_ERROR: { title: 'Download issue', message: 'Something went wrong with downloading file.' },

  // Chunk-upload service
  CHUNK_UPLOAD_ERROR: {
    title: 'Chunked Upload Error',
    message: 'Error while uploading a chunk.',
    timeout: 7000,
  },

  // Gl-upload
  GL_UPLOAD_ERROR: {
    title: 'GL upload error',
    message: 'Please verify that all columns values correspond to their column selectors',
    timeout: 7000,
  },

  // Capital account
  INVESTORS_UPLOAD_ERROR: {
    title: 'Investors upload error',
    message: 'Please review the uploaded data and ensure that all values match the template format.',
    timeout: 7000,
  },
  INVESTORS_LOAD_ERROR: {
    title: 'Investors loading error',
    message: 'Could not load the investors from the database.',
    timeout: 7000,
  },
  INVESTORS_DELETE_ERROR: {
    title: 'Investors deletion error',
    message: 'Could not delete the investor from the database.',
    timeout: 7000,
  },
  INVESTORS_EMPTY_FILE: {
    title: 'Investors upload error',
    message: 'No investors were found.',
    timeout: 7000,
  },
  INVESTORS_INVALID_ID: {
    title: 'Investors upload error',
    message: 'Please review the uploaded data and ensure that all investors`s IDs match the template format.',
    timeout: 7000,
  },
  INVESTORS_INVALID_COMMITMENT: {
    title: 'Investors upload error',
    message: 'Please review the uploaded data and ensure that all investors`s commitments match the template format.',
    timeout: 7000,
  },
  INVESTORS_MAX_ROW_AMOUNT_ERROR: {
    title: 'Error loading file: ',
    message: 'Maximum number of investors should not exceed ' + INVESTORS_MAX_ROW_AMOUNT,
    timeout: 7000,
  },
  INVESTORS_MISSING_FIELDS_ERROR: {
    title: 'Error loading file: ',
    message: 'Missing fields in Excel data.',
    timeout: 7000,
  },
  SELECT_ORGANIZATION_AND_FUND_ERROR: {
    title: 'Error adding Org and Fund IDs',
    message: 'Selected Org or Fund is not found.',
    timeout: 7000,
  },
  ITEMS_ALLOCATION_METHODS_LOAD_ERROR: {
    title: 'Error loading items allocation methods',
    message: 'Could not load items allocation methods.',
    timeout: 7000,
  },
  ITEMS_ALLOCATION_METHOD_SET_ERROR: {
    title: 'Error setting items allocation method',
    message: 'Could not set items allocation method.',
    timeout: 7000,
  },
  ITEM_VALUES_LOAD_ERROR: {
    title: 'Error loading item values',
    message: 'Could not load item values.',
    timeout: 7000,
  },
  ITEM_VALUES_UPLOAD_ERROR: {
    title: 'Error uploading item values',
    message: 'Could not upload item values.',
    timeout: 7000,
  },
  CUSTOM_ALLOCATIONS_LOAD_ERROR: {
    title: 'Error loading custom allocations',
    message: 'Could not load custom allocations.',
    timeout: 7000,
  },
  CUSTOM_ALLOCATIONS_UPLOAD_ERROR: {
    title: 'Error uploading custom allocations',
    message: 'Could not upload custom allocations.',
    timeout: 7000,
  },
  CUSTOM_ALLOCATION_DELETE_ERROR: {
    title: 'Error deleting custom allocation',
    message: 'Could not delete custom allocation.',
    timeout: 7000,
  },
  CUSTOM_ALLOCATION_VALUES_LOAD_ERROR: {
    title: 'Error loading custom allocation values',
    message: 'Could not load custom allocation values.',
    timeout: 7000,
  },
  CUSTOM_ALLOCATION_VALUES_UPLOAD_ERROR: {
    title: 'Error uploading custom allocation values',
    message: 'Could not upload custom allocation values.',
    timeout: 7000,
  },
  CARRIED_INTEREST_ALLOCATION_IS_NOT_MANUAL_ERROR: {
    title: 'Carried interest update error',
    message: 'Carried interest allocation method is not manual. Please, set it to manual first.',
    timeout: 7000,
  },

  // UPLOAD
  URL_FOR_UPLOAD: { title: 'URL-uploading error', message: 'Cant create URL for uploading' },
  CLOUD_UPLOAD: { title: 'Cloud error', message: 'Cant upload image to the cloud' },

  // TICKER
  DELETE_TICKER_ERROR: {
    title: 'Ticker error',
    message: 'Could not delete selected ticker.',
    timeout: 7000,
  },
  SAVE_TICKER_ERROR: {
    title: 'Ticker error',
    message: 'Could not save selected ticker.',
    timeout: 7000,
  },
  GET_TICKER_DATA_ERROR: {
    title: 'Ticker error',
    message: 'Could not get data for the selected ticker.',
    timeout: 7000,
  },
  NO_RELATED_TICKERS_FOUND: {
    title: 'No new related tickers were found.',
    message: 'Please try extending your company description or searching manually.',
    timeout: 7000,
  },


  // MODELS
  MODEL_NAME_ALREADY_EXISTS: { title: 'Warning', message: 'Model with this name already exists' },
  MODEL_NAME_EMPTY: { title: 'Warning', message: 'Please fill-in the name of the model.' },
  // Commitment allocation
  COMPANY_NAME_EMPTY: { title: 'Warning', message: 'Please fill-in the name of the company.' },
  ONE_ROUND_EXIT_VALUE_NOT_100: { title: 'Warning', message: 'If only one exit round is specified, please ensure its probability is 100%.' },
  TWO_ROUNDS_EXIT_VALUE_NOT_100: { title: 'Warning', message: 'If two exit rounds are specified, please ensure their combined probability sums up to 100%.' },
  THREE_ROUNDS_EXIT_VALUE_NOT_100: { title: 'Warning', message: 'Please ensure all predicted rounds probabilities sum up to 100%.' },

  // BUSINESS EVALUATION
  WEIGHT_UPDATED_SUCCESS: { title: 'Success', message: 'Weight was updated successfully.' },
  RANKING_UPDATED_SUCCESS: { title: 'Success', message: 'Ranking was updated successfully.' },
  EVALUATION_COMMENT_UPDATED_SUCCESS: { title: 'Success', message: 'Comment was updated successfully.' },
  EVALUATION_LIST_UPDATED_SUCCESS: { title: 'Success', message: 'Portfolio evaluations were updated successfully.' },
  EVALUATION_LIST_CREATED_SUCCESS: { title: 'Success', message: 'Portfolio evaluation were created successfully.' },
  EVALUATION_CREATED_SUCCESS: { title: 'Success', message: 'Portfolio evaluation was created successfully.' },
  EVALUATION_DELETED_SUCCESS: { title: 'Success', message: 'Portfolio evaluation was deleted successfully.' },
  EVALUATION_PARAMETER_UPDATED_SUCCESS: { title: 'Success', message: 'Parameter name was updated successfully.' },
  EVALUATION_COMMENT_MAX_SIZE_ERROR: { title: 'Error', message: 'Comment is too long. Please make it shorter.' },
  EVALUATION_NAME_ALREADY_EXISTS: { title: 'Warning', message: 'Parameter with this name already exists.' },
  EVALUATION_AI_ERROR: { title: 'Unable to evaluate the parameters', message: 'Please check the provided file or try again later' },

  // VALUATION DECISION
  DECISION_UPDATED_SUCCESS: { title: 'Success', message: 'Valuation decision was updated successfully.' },
};

/* eslint-enable @typescript-eslint/naming-convention */
/* eslint-enable max-len */

/**
 * Service to handle notifications in the application.
 *
 * @see https://ng.ant.design/components/notification/en.
 *
 * P.S: I've created this service to prevent everyone from writing
 * the same code for the same notifications. So notifications will be easier to manage.
 * Although, each notification call can be customized by `options` field in each method.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  notification = new Subject<NotificationProps>();

  /**
   * Show a success notification.
   *
   * @param content - The content of the notification.
   * @example
   * this.notifications.success({
   *   title: 'Success',
   *   message: 'The operation was successful.',
   * });
   */
  success(content: NotificationContent): void {
    this.notification.next(new NotificationProps(content, 'success'));
  }

  /**
   * Show an info notification.
   *
   * @see {@link success} Api is the same for this method.
   */
  info(content: NotificationContent): void {
    this.notification.next(new NotificationProps(content, 'info'));
  }

  /**
   * Show a warning notification.
   *
   * @see {@link success} Api is the same for this method.
   */
  warning(content: NotificationContent): void {
    this.notification.next(new NotificationProps(content, 'warning'));
  }


  /**
   * Show an error notification.
   *
   * @see {@link success} Api is the same for this method.
   */
  error(content: NotificationContent): void {
    this.notification.next(new NotificationProps(content, 'error'));
  }
}

export class NotificationProps {
  title: string;
  message: string;
  type: string;
  hidden: boolean;
  timeout: number;
  constructor(content: NotificationContent, type: string) {
    this.title = content.title;
    this.message = content.message;
    this.type = type;
    this.hidden = false;
    this.timeout = content.timeout || 3500;
  }
}
